import React from 'react';
import styled, { keyframes } from 'styled-components';
import useScreenSize from '../../hooks/useScreenSize';

// Create a pulsing animation for the skeleton elements
const pulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 0.6;
  }
`;

// Base skeleton element with common styles
export const SkeletonBase = styled.div`
  filter: ${({ theme }) => theme.mode === 'dark' ? 'invert(1)' : 'none'};
  background-color: #c0c0c0;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

// Skeleton Label - 50% width, 0.5rem height
const SkeletonLabel = styled(SkeletonBase)`
  width: 50%;
  height: 1.5rem;
`;

// Skeleton Label - 50% width, 0.5rem height
const SkeletonSearch = styled(SkeletonBase)`
  width: 25%;
  height: 2rem;
`;

// Skeleton Field - 100% width, 1rem height
const SkeletonField = styled(SkeletonBase)`
  width: 100%;
  height: 2.5rem;
`;

// Container for the skeleton elements
const SkeletonContainer = styled.div`
  width: 100%;
  padding: ${({ $compact }) => $compact ? '0' : '0.5rem'};

  ${({ $compact }) => $compact && `
    > *:last-child {
      margin-bottom: 0;
    }
  `}
`;

// Container for the skeleton table
const SkeletonTableContainer = styled.div`
  width: 100%;
  padding: 0.5rem;
`;

// Grid container for table columns
const SkeletonTableGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$ismobile ? '1fr' : 'repeat(4, 1fr)'};
  gap: 1rem;
  margin-top: 1rem;
`;

// Individual column container
const SkeletonTableColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Skeleton table header label
const SkeletonTableHeader = styled(SkeletonBase)`
  width: 100%;
  height: 1.5rem;
  margin-bottom: 0.75rem;
`;

// Skeleton table field
const SkeletonTableField = styled(SkeletonBase)`
  width: 100%;
  height: 2.5rem;
`;

/**
 * SkeletonLoader Component
 * 
 * Renders a configurable skeleton loading UI with labels and fields
 * 
 * @param {Object} props - Component props
 * @param {Array} props.config - Configuration array where:
 *   - 'L' represents a Label (50% width, 0.5rem height)
 *   - 'F' represents a Field (100% width, 1rem height)
 *   Example: ['L', 'F', 'L', 'F', 'F']
 * @returns {JSX.Element} - Rendered component
 */
const SkeletonLoader = ({ config = ['L', 'F', 'L', 'F', 'F'], compact = false }) => {
  return (
    <SkeletonContainer $compact={compact}>
      {config.map((type, index) => {
        if (type === 'L') {
          return <SkeletonLabel key={`skeleton-${index}`} />;
        } else if (type === 'F') {
          return <SkeletonField key={`skeleton-${index}`} />;
        }
        return null;
      })}
    </SkeletonContainer>
  );
};

/**
 * SkeletonTable Component
 * 
 * Renders a skeleton loading UI for table-like data with column headers and rows
 * Displays in 4 columns (each with 1 header + 10 fields) on desktop and 1 column on mobile
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Optional label text to display above the table
 * @returns {JSX.Element} - Rendered component
 */
const SkeletonTable = ({ label = "Loading data..." }) => {
  const { isMobile } = useScreenSize();

  // Generate columns based on mobile/desktop
  const columnCount = isMobile ? 1 : 4;

  const columns = Array.from({ length: columnCount }, (_, columnIndex) => {
    // Generate 10 skeleton fields for each column
    const fields = Array.from({ length: 10 }, (_, fieldIndex) => (
      <SkeletonTableField key={`skeleton-table-field-${columnIndex}-${fieldIndex}`} />
    ));

    return (
      <SkeletonTableColumn key={`skeleton-table-column-${columnIndex}`}>
        <SkeletonTableHeader />
        {fields}
      </SkeletonTableColumn>
    );
  });

  return (
    <SkeletonTableContainer>
      <SkeletonTableGrid $ismobile={isMobile}>
        {columns}
      </SkeletonTableGrid>
    </SkeletonTableContainer>
  );
};

export default SkeletonLoader;
export { SkeletonTable };
